import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  Upload, FileText, Download, Trash2, Edit2, Search,
  Tag, Calendar, User, MoreVertical, FolderOpen, Eye,
  Filter, X, Plus, RefreshCw
} from 'lucide-react';

const ClienteLegajoDocumentos = ({ clienteCuit }) => {
  const [documentos, setDocumentos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  
  // Filtros
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [filtroEtiqueta, setFiltroEtiqueta] = useState('');
  const [busqueda, setBusqueda] = useState('');
  
  // Dialogs
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  
  // Upload form
  const [uploadForm, setUploadForm] = useState({
    file: null,
    category_id: '',
    descripcion: '',
    etiquetas: ''
  });

  const loadDocumentos = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filtroCategoria) params.append('category_id', filtroCategoria);
      if (filtroEtiqueta) params.append('etiqueta', filtroEtiqueta);
      
      const response = await api.get(`/clientes/${clienteCuit}/documentos?${params}`);
      setDocumentos(response.data.documentos || []);
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  }, [clienteCuit, filtroCategoria, filtroEtiqueta]);

  const loadCategorias = async () => {
    try {
      const response = await api.get('/clientes/documentos/categorias');
      setCategorias(response.data.categorias || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  useEffect(() => {
    loadCategorias();
  }, []);

  useEffect(() => {
    if (clienteCuit) {
      loadDocumentos();
    }
  }, [clienteCuit, loadDocumentos]);

  const handleUpload = async () => {
    if (!uploadForm.file || !uploadForm.category_id) {
      toast.error('Selecciona un archivo y una categoría');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('category_id', uploadForm.category_id);
      formData.append('descripcion', uploadForm.descripcion);
      formData.append('etiquetas', uploadForm.etiquetas);
      
      await api.post(`/clientes/${clienteCuit}/documentos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Documento subido correctamente');
      setShowUploadDialog(false);
      setUploadForm({ file: null, category_id: '', descripcion: '', etiquetas: '' });
      loadDocumentos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir documento');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await api.get(`/clientes/documentos/${doc.id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Error al descargar documento');
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('¿Eliminar este documento del legajo?')) return;
    
    try {
      await api.delete(`/clientes/documentos/${docId}`);
      toast.success('Documento eliminado');
      loadDocumentos();
    } catch (error) {
      toast.error('Error al eliminar documento');
    }
  };

  const handleUpdateDoc = async () => {
    if (!selectedDoc) return;
    
    try {
      await api.put(`/clientes/documentos/${selectedDoc.id}`, {
        descripcion: selectedDoc.descripcion,
        etiquetas: selectedDoc.etiquetas,
        category_id: selectedDoc.category_id
      });
      
      toast.success('Documento actualizado');
      setShowEditDialog(false);
      loadDocumentos();
    } catch (error) {
      toast.error('Error al actualizar documento');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCategoryColor = (categoryId) => {
    const cat = categorias.find(c => c.id === categoryId);
    return cat?.color || '#6B7280';
  };

  const filteredDocs = documentos.filter(doc => {
    if (busqueda) {
      const search = busqueda.toLowerCase();
      return doc.filename.toLowerCase().includes(search) ||
             doc.descripcion?.toLowerCase().includes(search) ||
             doc.etiquetas?.some(t => t.toLowerCase().includes(search));
    }
    return true;
  });

  // Agrupar documentos por categoría para la vista
  const docsByCategory = filteredDocs.reduce((acc, doc) => {
    const cat = doc.category_id || 'otros';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(doc);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      {/* Header con acciones */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-slate-900">Legajo de Documentos</h3>
          <Badge variant="secondary">{documentos.length} documentos</Badge>
        </div>
        <Button onClick={() => setShowUploadDialog(true)} className="gap-2">
          <Upload size={16} />
          Subir Documento
        </Button>
      </div>
      
      {/* Filtros */}
      <Card className="p-4 bg-white">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
              <Input
                placeholder="Buscar en documentos..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          
          <Select value={filtroCategoria || "all"} onValueChange={(v) => setFiltroCategoria(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Todas las categorías" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las categorías</SelectItem>
              {categorias.map(cat => (
                <SelectItem key={cat.id} value={cat.id}>
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: cat.color }}
                    />
                    {cat.nombre}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {(filtroCategoria || filtroEtiqueta || busqueda) && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => {
                setFiltroCategoria('');
                setFiltroEtiqueta('');
                setBusqueda('');
              }}
            >
              <X size={16} className="mr-1" />
              Limpiar filtros
            </Button>
          )}
        </div>
      </Card>
      
      {/* Lista de documentos agrupados por categoría */}
      {loading ? (
        <div className="text-center py-12">
          <RefreshCw className="animate-spin mx-auto text-slate-400" size={32} />
          <p className="text-slate-500 mt-2">Cargando documentos...</p>
        </div>
      ) : filteredDocs.length === 0 ? (
        <Card className="p-12 text-center bg-white">
          <FolderOpen className="mx-auto text-slate-300 mb-4" size={48} />
          <p className="text-slate-500 font-medium">No hay documentos en el legajo</p>
          <p className="text-sm text-slate-400 mt-1">
            Sube el primer documento del cliente
          </p>
          <Button 
            className="mt-4" 
            onClick={() => setShowUploadDialog(true)}
          >
            <Upload size={16} className="mr-2" />
            Subir Documento
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {categorias.filter(cat => docsByCategory[cat.id]?.length > 0).map(cat => (
            <Card key={cat.id} className="bg-white overflow-hidden">
              <div 
                className="px-4 py-3 border-b flex items-center gap-3"
                style={{ borderLeftWidth: '4px', borderLeftColor: cat.color }}
              >
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: cat.color }}
                />
                <h4 className="font-medium text-slate-900">{cat.nombre}</h4>
                <Badge variant="secondary" className="ml-auto">
                  {docsByCategory[cat.id].length}
                </Badge>
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Archivo</TableHead>
                    <TableHead>Descripción</TableHead>
                    <TableHead>Etiquetas</TableHead>
                    <TableHead>Tamaño</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Subido por</TableHead>
                    <TableHead className="w-[100px]">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {docsByCategory[cat.id].map(doc => (
                    <TableRow key={doc.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <FileText size={16} className="text-slate-400" />
                          <span className="font-medium text-slate-900 truncate max-w-[200px]">
                            {doc.filename}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600 truncate max-w-[200px]">
                        {doc.descripcion || '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {doc.etiquetas?.slice(0, 3).map((tag, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                          {doc.etiquetas?.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{doc.etiquetas.length - 3}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm">
                        {formatFileSize(doc.size)}
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm">
                        {formatDate(doc.uploaded_at)}
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm truncate max-w-[150px]">
                        {doc.uploaded_by}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical size={16} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleDownload(doc)}>
                              <Download size={14} className="mr-2" />
                              Descargar
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                              setSelectedDoc({...doc});
                              setShowEditDialog(true);
                            }}>
                              <Edit2 size={14} className="mr-2" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              className="text-red-600"
                              onClick={() => handleDelete(doc.id)}
                            >
                              <Trash2 size={14} className="mr-2" />
                              Eliminar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          ))}
          
          {/* Documentos sin categoría */}
          {docsByCategory['otros']?.length > 0 && (
            <Card className="bg-white overflow-hidden">
              <div className="px-4 py-3 border-b border-l-4 border-l-slate-400 flex items-center gap-3">
                <FolderOpen size={16} className="text-slate-400" />
                <h4 className="font-medium text-slate-900">Otros</h4>
                <Badge variant="secondary" className="ml-auto">
                  {docsByCategory['otros'].length}
                </Badge>
              </div>
              {/* ... similar table for otros */}
            </Card>
          )}
        </div>
      )}
      
      {/* Dialog: Subir Documento */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Subir Documento al Legajo</DialogTitle>
            <DialogDescription>
              Agrega un nuevo documento al legajo del cliente
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Archivo *</Label>
              <Input
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                onChange={(e) => setUploadForm({
                  ...uploadForm, 
                  file: e.target.files[0]
                })}
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">
                PDF, Word, Excel o imágenes. Máx 10MB.
              </p>
            </div>
            
            <div>
              <Label>Categoría *</Label>
              <Select 
                value={uploadForm.category_id} 
                onValueChange={(v) => setUploadForm({...uploadForm, category_id: v})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecciona categoría" />
                </SelectTrigger>
                <SelectContent>
                  {categorias.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: cat.color }}
                        />
                        {cat.nombre}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Descripción</Label>
              <Textarea
                value={uploadForm.descripcion}
                onChange={(e) => setUploadForm({...uploadForm, descripcion: e.target.value})}
                placeholder="Descripción del documento..."
                rows={2}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Etiquetas</Label>
              <Input
                value={uploadForm.etiquetas}
                onChange={(e) => setUploadForm({...uploadForm, etiquetas: e.target.value})}
                placeholder="vigente, firmado, original (separadas por coma)"
                className="mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleUpload} disabled={uploading}>
              {uploading ? (
                <>
                  <RefreshCw className="animate-spin mr-2" size={16} />
                  Subiendo...
                </>
              ) : (
                <>
                  <Upload size={16} className="mr-2" />
                  Subir
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog: Editar Documento */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Documento</DialogTitle>
            <DialogDescription>
              Modifica la información del documento
            </DialogDescription>
          </DialogHeader>
          
          {selectedDoc && (
            <div className="space-y-4">
              <div>
                <Label className="text-slate-500">Archivo</Label>
                <p className="font-medium">{selectedDoc.filename}</p>
              </div>
              
              <div>
                <Label>Categoría</Label>
                <Select 
                  value={selectedDoc.category_id} 
                  onValueChange={(v) => setSelectedDoc({...selectedDoc, category_id: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categorias.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: cat.color }}
                          />
                          {cat.nombre}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Descripción</Label>
                <Textarea
                  value={selectedDoc.descripcion || ''}
                  onChange={(e) => setSelectedDoc({...selectedDoc, descripcion: e.target.value})}
                  rows={2}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Etiquetas</Label>
                <Input
                  value={selectedDoc.etiquetas?.join(', ') || ''}
                  onChange={(e) => setSelectedDoc({
                    ...selectedDoc, 
                    etiquetas: e.target.value.split(',').map(t => t.trim()).filter(Boolean)
                  })}
                  className="mt-1"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleUpdateDoc}>
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClienteLegajoDocumentos;
